import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

const STATUS_BADGE = {
  draft: 'bg-neutral-200 text-neutral-600',
  published: 'bg-success-subtle text-success-emphasis',
  disabled: 'bg-warning-subtle text-warning-emphasis',
  archived: 'bg-neutral-200 text-neutral-600',
};

const actionIconStyle = { width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 };
const solidGreen = { backgroundColor: '#198754', borderColor: '#198754', color: '#fff', opacity: 1 };
const solidRed = { backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff', opacity: 1 };
const solidOrange = { backgroundColor: '#fd7e14', borderColor: '#fd7e14', color: '#fff', opacity: 1 };

function AdminMockExams() {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchCourses = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/courses`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) setCourses(response.data.courses || []);
    } catch (err) { /* non-fatal */ }
  }, []);

  const fetchExams = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        setError('Authentication required.');
        setLoading(false);
        return;
      }
      const params = {};
      if (courseFilter) params.courseId = courseFilter;
      if (statusFilter) params.status = statusFilter;
      const response = await axios.get(`${apiUrl}/api/admin/mock-exams`, { headers: { Authorization: `Bearer ${token}` }, params });
      if (response.data.success) setExams(response.data.exams || []);
      else setError(response.data.message || 'Failed to fetch exams.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching exams.');
    } finally {
      setLoading(false);
    }
  }, [courseFilter, statusFilter]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);
  useEffect(() => { fetchExams(); }, [fetchExams]);

  const publishExam = async (exam) => {
    setError('');
    setSuccess('');
    try {
      const token = getToken();
      const response = await axios.put(`${apiUrl}/api/admin/mock-exams/${exam.id}/publish`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        setSuccess('Exam published.');
        fetchExams();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError((response.data.errors || [response.data.message]).join(' '));
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      setError(errors ? errors.join(' ') : (err.response?.data?.message || 'Error publishing exam.'));
    }
  };

  const disableExam = async (exam) => {
    if (!window.confirm(`Disable "${exam.title}"? It will no longer appear to students, but in-progress attempts may still be submitted.`)) return;
    try {
      const token = getToken();
      await axios.put(`${apiUrl}/api/admin/mock-exams/${exam.id}/disable`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchExams();
    } catch (err) {
      setError(err.response?.data?.message || 'Error disabling exam.');
    }
  };

  const archiveExam = async (exam) => {
    if (!window.confirm(`Archive "${exam.title}"? It will be kept for history but cannot be taken again.`)) return;
    try {
      const token = getToken();
      await axios.put(`${apiUrl}/api/admin/mock-exams/${exam.id}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchExams();
    } catch (err) {
      setError(err.response?.data?.message || 'Error archiving exam.');
    }
  };

  const deleteExam = async (exam) => {
    if (!window.confirm(`Delete draft exam "${exam.title}"? This cannot be undone.`)) return;
    try {
      const token = getToken();
      const response = await axios.delete(`${apiUrl}/api/admin/mock-exams/${exam.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) fetchExams();
      else setError(response.data.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting exam.');
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Mock Exams</span></li>
              </ul>
            </div>
            <button type="button" className="btn btn-main rounded-pill py-9" onClick={() => navigate('/admin-mock-exam-form')}>
              <i className="ph ph-plus me-2" /> Create Exam
            </button>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          <div className="card mb-24">
            <div className="card-body">
              <div className="row g-16">
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Course</label>
                  <select className="form-control py-9" value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
                    <option value="">All Courses</option>
                    {courses.map((c) => (<option key={c.id} value={c.id}>{c.coursename}</option>))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Status</label>
                  <select className="form-control py-9" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="disabled">Disabled</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">Exams ({exams.length})</h5>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : exams.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No exams yet. Create one above.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Course</th>
                        <th>Questions</th>
                        <th>Status</th>
                        <th>Attempts</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exams.map((ex) => (
                        <tr key={ex.id}>
                          <td className="fw-semibold" style={{ maxWidth: 180 }}>{ex.title}</td>
                          <td style={{ maxWidth: 180, whiteSpace: 'normal' }} title={ex.coursename}>{ex.coursename}</td>
                          <td>{ex.total_questions_configured}</td>
                          <td><span className={`badge ${STATUS_BADGE[ex.status] || ''}`}>{ex.status}</span></td>
                          <td>{ex.total_attempts}</td>
                          <td>
                            <div className="d-flex gap-6 flex-wrap">
                              <button type="button" className="btn btn-sm btn-outline-main py-6 px-8" style={actionIconStyle} title="Edit" onClick={() => navigate(`/admin-mock-exam-form/${ex.id}`)}>
                                <i className="ph ph-pencil-simple" />
                              </button>
                              {ex.status !== 'published' && ex.status !== 'archived' && (
                                <button type="button" className="btn btn-sm py-6 px-8" style={{ ...actionIconStyle, ...solidGreen }} title="Publish" onClick={() => publishExam(ex)}>
                                  <i className="ph ph-rocket-launch" />
                                </button>
                              )}
                              {ex.status === 'published' && (
                                <button type="button" className="btn btn-sm py-6 px-8" style={{ ...actionIconStyle, ...solidOrange }} title="Disable" onClick={() => disableExam(ex)}>
                                  <i className="ph ph-pause" />
                                </button>
                              )}
                              {ex.status === 'disabled' && (
                                <button type="button" className="btn btn-sm py-6 px-8" style={{ ...actionIconStyle, ...solidGreen }} title="Re-publish" onClick={() => publishExam(ex)}>
                                  <i className="ph ph-rocket-launch" />
                                </button>
                              )}
                              {ex.status !== 'archived' && (
                                <button type="button" className="btn btn-sm btn-outline-main py-6 px-8" style={actionIconStyle} title="Archive" onClick={() => archiveExam(ex)}>
                                  <i className="ph ph-archive" />
                                </button>
                              )}
                              <button type="button" className="btn btn-sm btn-outline-main py-6 px-8" style={actionIconStyle} title="Attempts" onClick={() => navigate(`/admin-mock-exam-attempts/${ex.id}`)}>
                                <i className="ph ph-list-checks" />
                              </button>
                              {ex.status === 'draft' && ex.total_attempts === 0 && (
                                <button type="button" className="btn btn-sm py-6 px-8" style={{ ...actionIconStyle, ...solidRed }} title="Delete" onClick={() => deleteExam(ex)}>
                                  <i className="ph ph-trash" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminMockExams;
